import React, {FC, useEffect, useState} from "react"
import PropTypes from "prop-types"

import {Body, H1, H3, Label} from "@irati/ui/src/Typography"
import {Col} from "@irati/ui/src/Layout"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import {FormattedCurrency, FormattedMessage, useIntl} from "@irati/utils/lang"
import Link from "@irati/ui/src/Link"
import Spacing from "@irati/ui/src/Spacing"
import Button from "@irati/ui/src/Button"
import {Deal, PaymentProvider} from "@irati/types"
import PayByWalletCheckBox from "@irati-web/payments/src/components/PerksPaymentSelect/PayByWalletCheckBox"
import PaymentForm from "@irati-web/payments/src/components/PaymentFormV2"
import {useCheckout} from "@irati/checkout/hooks"
import Loading from "@irati/ui/src/Loading"
import Loader from "@irati/ui/src/Loading/Loader"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import history from "@irati/utils/history"
import {getDomain} from "@irati/utils/links"
import authentication from "@irati/auth"
import {hangPromise} from "@irati/utils/promise"
import {PerksPaymentSelectOption} from "@shared/payments/types"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"

interface Props extends WithAuth {
  deal: Deal
  updatePaymentBreakdown: (options: PerksPaymentSelectOption[]) => void
}

const CheckoutStep: FC<Props> = ({deal, updatePaymentBreakdown, auth}, context) => {
  const {formatMessage} = useIntl()
  const [paymentLoad, setPaymentLoad] = useState<Promise<void>>()
  const [useWallet, setUseWallet] = useState(true)

  const {checkout, wallet, checkoutPayment, load: checkoutAndWalletLoad, onPaymentComplete} = useCheckout({useWallet, deal})

  const totalAmount = checkout?.basket.total.amount
  const totalAmountBeforeDiscount = checkout?.basket.total.originalPrice
  const totalCurrency = checkout?.paymentOrder.currency
  const availableWalletBalance = wallet?.availableBalanceInCents || 0
  const isCashPaymentNeeded = checkoutPayment?.amount > 0
  const isWalletFullPayment = !isCashPaymentNeeded && checkoutPayment?.walletAmount > 0

  const handlePaymentComplete = () => {
    const req = onPaymentComplete().then((details) => {
      const url = detailsToWebAddress(details)
      // Keep screen loading until the page has redirected
      setPaymentLoad(hangPromise())
      history.replace(url, context)
    })
    setPaymentLoad(req)
  }

  useEffect(() => {
    // Set wallet usage to display in sidebar OrderSummary, pass value to parent to use existing solution. Approach should be updated once Adyen will be removed completely
    if (checkoutPayment.walletAmount) {
      updatePaymentBreakdown([{type: PaymentProvider.Wallet, amount: checkoutPayment.walletAmount}])
    } else {
      updatePaymentBreakdown([])
    }
  }, [checkoutPayment.walletAmount])

  const termsLink =
    auth.user.regionId === "AU" ? "perks.checkout.termsAndConditions.linkHrefAu" : "perks.checkout.termsAndConditions.linkHref"
  return (
    <>
      <H1>
        <FormattedMessage id="perks.checkout.header" />
      </H1>

      <Loading load={checkoutAndWalletLoad} initLoad>
        <Col mdHidden lgHidden hidden={checkout?.basket.cashback.amount > 0}>
          <Flex justifyContent="space-between" alignItems="center">
            <Body large>
              <FormattedMessage id="perks.components.orderSummary.total.toPay" />
            </Body>
            <Flex alignItems="center">
              <Label color={mainTheme.colors.utility.error} textDecoration="line-through">
                <FormattedCurrency value={totalAmountBeforeDiscount} currency={totalCurrency} />
              </Label>
              <Body large marginLeft>
                <FormattedCurrency value={totalAmount} currency={totalCurrency} />
              </Body>
            </Flex>
          </Flex>
        </Col>
        <Label color={mainTheme.colors.greys.grey50} marginTop={0.5}>
          <FormattedMessage
            id="perks.checkout.termsAndConditions.copy"
            values={{
              link: (
                <Link href={formatMessage({id: termsLink})}>
                  <FormattedMessage id="perks.checkout.termsAndConditions.linkCopy" />
                </Link>
              )
            }}
          />
        </Label>

        <Loading
          load={paymentLoad}
          Loader={() => (
            <Flex direction="column">
              <H3 marginBottom>
                <FormattedMessage id="perks.checkout.loading.processPayment" />
              </H3>
              <Loader />
            </Flex>
          )}>
          <Spacing marginTop={2}>
            <PayByWalletCheckBox
              onChange={setUseWallet}
              value={useWallet}
              currency={wallet?.currency!}
              availableBalance={availableWalletBalance}
              remainingBalance={availableWalletBalance - (checkoutPayment.walletAmount || 0)}
            />

            {isWalletFullPayment && (
              <Flex justifyContent="center">
                <Button primary onClick={handlePaymentComplete} full>
                  <FormattedMessage id="perks.checkout.payByWallet.copy" />
                </Button>
              </Flex>
            )}
          </Spacing>

          <Spacing marginTop={2}>
            <PaymentForm
              amount={checkoutPayment?.amount}
              currency={checkoutPayment?.currency}
              onComplete={handlePaymentComplete}
              returnUrl={getRedirectUrl(deal.urlSlug!, checkout?.uuid)}
              paymentOrderId={checkoutPayment?.paymentOrderId}
            />
          </Spacing>
        </Loading>
      </Loading>
    </>
  )
}

CheckoutStep.contextTypes = {
  appPath: PropTypes.string
}

const getRedirectUrl = (dealSlug: string, checkoutId: string) => {
  const domain = getDomain(authentication.business().subdomain)
  return `${domain}/perks/info/${dealSlug}/payment-redirect/${checkoutId}`
}

export default withAuth(CheckoutStep)
